// Parse markdown assessment format into assessment object
// Cache buster: v2

export const parseAssessmentMarkdown = (markdown) => {
  const errors = [];
  const lines = markdown.split('\n').map(line => line.trim()).filter(line => line.length > 0);

  if (lines.length === 0) {
    throw new Error("Markdown is empty");
  }

  let assessment = {
    name: "",
    description: "",
    showResults: "scoreAndAnswers",
    allowRetake: true,
    version: "1.0",
    questions: [],
    scoreBands: []
  };

  let currentQuestion = null;
  let inScoreBands = false;

  console.log("📝 Parsing markdown with", lines.length, "lines");

  try {
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      console.log(`Line ${i}: "${line}"`);

    // DETAILS section
    if (line.startsWith("DETAILS;")) {
      const detailsMatch = line.match(/DETAILS;\s*(.+?),\s*(.+)$/);
      if (!detailsMatch) {
        errors.push("Invalid DETAILS format. Use: DETAILS; <title>,<description>");
        continue;
      }
      assessment.name = detailsMatch[1].trim();
      assessment.description = detailsMatch[2].trim();
      console.log(`✓ DETAILS: name="${assessment.name}", desc="${assessment.description}"`);
      continue;
    }

    // Optional DETAILS properties
    if (line.startsWith("Show Results:")) {
      assessment.showResults = line.replace("Show Results:", "").trim().toLowerCase();
      continue;
    }
    if (line.startsWith("Allow user to retake:")) {
      assessment.allowRetake = line.includes("true");
      continue;
    }
    if (line.startsWith("Version:")) {
      assessment.version = line.replace("Version:", "").trim();
      continue;
    }

    // Score bands section marker
    if (line.startsWith("SCOREBANDS:")) {
      if (currentQuestion) {
        validateQuestion(currentQuestion, errors);
        assessment.questions.push(currentQuestion);
        console.log(`✓ Saved final question before SCOREBANDS`);
        currentQuestion = null;
      }
      inScoreBands = true;
      console.log("→ Entering SCOREBANDS section");
      continue;
    }

    // Question section
    if (line.startsWith("Q:")) {
      if (currentQuestion) {
        validateQuestion(currentQuestion, errors);
        assessment.questions.push(currentQuestion);
        const optCount = currentQuestion.opts ? currentQuestion.opts.length : 0;
        console.log(`✓ Saved question: "${currentQuestion.q}" with ${optCount} options`);
      }
      currentQuestion = {
        id: "caq_" + Date.now(),
        q: line.replace("Q:", "").trim(),
        type: "scored",
        opts: [] // Ensure opts is always an array
      };
      inScoreBands = false;
      console.log(`→ New question: "${currentQuestion.q}"`);
      continue;
    }

    // Option line
    if (line.startsWith("O:") && currentQuestion && !inScoreBands) {
      const optionMatch = line.match(/O:\s*(.+?)\s*==\s*(\d+)\s*$/);
      if (!optionMatch) {
        errors.push(`Invalid option format: "${line}". Use: O: <option> == <score>`);
        continue;
      }
      const optionLabel = optionMatch[1].trim();
      const score = parseInt(optionMatch[2], 10);

      currentQuestion.opts.push({
        id: "cao_" + Date.now(),
        label: optionLabel,
        score: score
      });
      console.log(`  + Option: "${optionLabel}" = ${score}`);
      continue;
    }

    // Band line
    if (inScoreBands) {
      console.log(`  [inScoreBands=true] Checking line: "${line}"`);
    }
    if (line.startsWith("BAND:") && inScoreBands) {
      console.log(`📍 Parsing BAND line: "${line}"`);

      // Extract the content after "BAND: "
      const bandContent = line.replace("BAND:", "").trim();

      // Split by comma, but be careful with content
      const parts = bandContent.split(",");

      if (parts.length < 5) {
        console.error(`❌ BAND has too few parts (${parts.length}): "${line}"`);
        errors.push(`Invalid BAND format: "${line}". Need: <min>,<max>,<label>,<description>,<hexcolor>`);
        continue;
      }

      const min = parseInt(parts[0].trim(), 10);
      const max = parseInt(parts[1].trim(), 10);
      const label = parts[2].trim();
      // Join remaining parts in case description had commas
      const description = parts.slice(3, -1).join(",").trim();
      const color = parts[parts.length - 1].trim();

      // Validate hex color
      if (!/^#[0-9A-Fa-f]{6}$/.test(color)) {
        console.error(`❌ Invalid color format: "${color}"`);
        errors.push(`Invalid color format in BAND: "${color}". Use format: #RRGGBB`);
        continue;
      }

      if (isNaN(min) || isNaN(max)) {
        console.error(`❌ Invalid min/max: min=${min}, max=${max}`);
        errors.push(`Invalid min/max scores in BAND: min=${parts[0]}, max=${parts[1]}`);
        continue;
      }

      if (min > max) {
        console.error(`❌ Min > Max: ${min} > ${max}`);
        errors.push(`Score band min (${min}) cannot be greater than max (${max})`);
        continue;
      }

      assessment.scoreBands.push({
        id: "csb_" + Date.now(),
        min: min,
        max: max,
        label: label,
        desc: description,
        color: color
      });
      console.log(`✓ Band: ${min}-${max} "${label}"`);
      continue;
    }
    }
  } catch (loopErr) {
    console.error("💥 Error during parsing loop:", loopErr);
    errors.push(`Parsing error: ${loopErr.message}`);
  }

  // Save last question if exists
  if (currentQuestion) {
    validateQuestion(currentQuestion, errors);
    assessment.questions.push(currentQuestion);
    const optCount = currentQuestion.opts ? currentQuestion.opts.length : 0;
    console.log(`✓ Saved final question: "${currentQuestion.q}" with ${optCount} options`);
  }

  // Validation
  if (!assessment.name) {
    errors.push("Assessment name is required in DETAILS section");
  }
  if (!assessment.description) {
    errors.push("Assessment description is required in DETAILS section");
  }
  if (assessment.questions.length === 0) {
    errors.push("At least one question is required");
  }
  if (assessment.scoreBands.length === 0) {
    errors.push("At least one score band is required");
  }

  console.log(`\n📊 Parsing complete: ${assessment.questions.length} questions, ${assessment.scoreBands.length} bands, ${errors.length} errors`);

  if (errors.length > 0) {
    throw new Error(`Markdown parsing errors:\n${errors.join("\n")}`);
  }

  return assessment;
};

function validateQuestion(question, errors) {
  if (!question.q) {
    errors.push("Question text is missing");
    return;
  }
  if (question.opts.length < 2) {
    errors.push(`Question "${question.q}" has fewer than 2 options (has ${question.opts.length})`);
    return;
  }

  // Check for duplicate scores
  const scores = question.opts.map(o => o.score);
  const uniqueScores = new Set(scores);
  if (scores.length !== uniqueScores.size) {
    errors.push(`Question "${question.q}" has duplicate scores`);
  }
}
